import { useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import isEmpty from 'lodash/isEmpty';
import { useContentTags } from './useContentTags';
import { CONTENT_TAG_CATEGORIES_MAP } from '../../utils/constants';
import { makeAliasesFromClassAndOrg } from './make-aliases-from';
import { getCustomTagNameByIdFor } from './get-custom-tag-name-by-id-for';
import { makeTreeToRootFromTags } from './make-tree-from-tags';
import { getSubthemeContentTree } from '../../utils/content-tags';

export function useGetCurriculumRootTagById() {
  const { globalCurriculumTagsById } = useContentTags();

  return useCallback(
    (contentTagId) => {
      let rootTag;

      for (
        let tag = globalCurriculumTagsById[contentTagId];
        tag;
        tag = globalCurriculumTagsById[tag.parentId]
      ) {
        rootTag = tag;
      }

      return rootTag;
    },
    [globalCurriculumTagsById]
  );
}

export function useGlobalTopicTree(curriculumLevelId) {
  const { globalMeeoppTopicTree } = useContentTags();
  const getCurriculumRootTagById = useGetCurriculumRootTagById();

  const user = useSelector(({ auth }) => auth.user);

  const getAliasedContentTags = useCallback(
    (contentTag) => {
      const aliases = makeAliasesFromClassAndOrg(user);
      const customTagNameById = getCustomTagNameByIdFor(aliases);

      return {
        ...contentTag,
        title: customTagNameById[contentTag._id] ?? contentTag.displayTitle ?? contentTag.title,
      };
    },
    [user]
  );

  function getThemeOptions(themeTags) {
    const rawTree = isEmpty(themeTags) ? globalMeeoppTopicTree : themeTags;

    let queue = [...rawTree];
    let contentTags = [];
    let contentTagsMapById = {};

    while (queue.length > 0) {
      let current = queue.shift();
      const aliasedTag = getAliasedContentTags(current);
      contentTagsMapById[current._id] = aliasedTag;
      contentTags.push(aliasedTag);
      queue.push(...current.children);
    }

    const processedTree = makeTreeToRootFromTags(contentTags, contentTagsMapById);
    return getSubthemeContentTree(processedTree);
  }

  return useMemo(() => {
    if (!curriculumLevelId) {
      return getThemeOptions();
    }

    const curriculumTag = getCurriculumRootTagById(curriculumLevelId);

    if (!curriculumTag) {
      return getThemeOptions();
    }

    const themeTags = curriculumTag.children.filter((tag) => {
      return tag.category === CONTENT_TAG_CATEGORIES_MAP.theme;
    });

    return getThemeOptions(themeTags);
  }, [curriculumLevelId, globalMeeoppTopicTree, getCurriculumRootTagById, getAliasedContentTags]);
}
